import React, { useState, useEffect } from 'react';
import { X, Bold, Italic, Link, List, Image, Layout, FileText, UploadCloud, ChevronDown, CheckSquare, Square, File, Loader2 } from 'lucide-react';
import Select from 'react-select';
import '../styles/ShareResumeModal.css';
import BASE_URL from '../../url';

const ShareResumeModal = ({ isOpen, onClose, candidates, onSuccess, autoPopulateCandidateEmails = false, roleName, roleId, isBulkEmailMode = false }) => {
  const [template, setTemplate] = useState('Resume Share');
  const [toInput, setToInput] = useState('');
  const [ccList, setCcList] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [subject, setSubject] = useState('Resume Share User Template');
  
  // Dummy content based on the candidate prop
  const [editorContent, setEditorContent] = useState('');
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);

  // Bulk Email Mode Role State
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Reset state when modal closes, and auto-populate CC when it opens
  useEffect(() => {
    if (!isOpen) {
      setSelectedRole(null);
      setCcList([]);
      setCcInput('');
      setBccInput('');
      setSubject('Resume Share User Template');
    } else {
      try {
        const userStr = localStorage.getItem('user');
        let emailFound = null;
        if (userStr) {
          const userObj = JSON.parse(userStr);
          if (userObj && userObj.email) emailFound = userObj.email;
          else if (userObj && userObj.Email) emailFound = userObj.Email;
        }
        
        if (!emailFound) {
          const storedUsername = localStorage.getItem('username');
          if (storedUsername && storedUsername.includes('@')) {
            emailFound = storedUsername;
          }
        }
        if (emailFound) {
          // Split by comma in case the stored email has multiple addresses
          const emails = emailFound.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean);
          if (emails.length > 0) {
            setCcList(emails);
          }
        }
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isBulkEmailMode && isOpen && availableRoles.length === 0) {
      const fetchRoles = async () => {
        try {
          setLoadingRoles(true);
          const token = localStorage.getItem('token');
          const response = await fetch(`${BASE_URL}/api/recruitment/roles`, { 
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setAvailableRoles(data);
          }
        } catch (e) {
          console.error('Error fetching roles:', e);
        } finally {
          setLoadingRoles(false);
        }
      };
      fetchRoles();
    }
  }, [isBulkEmailMode, isOpen, availableRoles.length]);

  useEffect(() => {
    // If a role is selected from the internal dropdown, override the subject and content
    if (selectedRole) {
      setSubject(`Job Opportunity: ${selectedRole.title || selectedRole.role}`);
      const jobLink = selectedRole.id ? `\n\nYou can view the full Job Description on our careers portal here:\n${window.location.origin}/job/${selectedRole.id}` : '';
      const roleDetails = [
        `Role       : ${selectedRole.title || selectedRole.role || 'N/A'}`,
        `Location   : ${selectedRole.location || selectedRole.city || 'N/A'}`,
        `Work Mode  : ${selectedRole.roleLocation || selectedRole.workMode || 'N/A'}`,
        `Experience : ${selectedRole.experience || 'N/A'}`,
      ].join('\n');
      setEditorContent(`Hi,\n\nWe have an excellent opportunity for the ${selectedRole.title || selectedRole.role} role. We reviewed your resume and found your profile to be very suitable for this position.\n\n${roleDetails}${jobLink}\n\nWe would like to schedule a call with you to discuss this opportunity further.`);
    }
  }, [selectedRole]);

  useEffect(() => {
    if (roleName) {
      setSubject(`Job Opportunity: ${roleName}`);
    }
  }, [roleName]);

  useEffect(() => {
    if (candidates && candidates.length > 0) {
      // Don't overwrite if selectedRole is handling it
      if (!selectedRole) {
        if (roleName) {
          const jobLink = roleId ? `\n\nYou can view the full Job Description on our careers portal here:\n${window.location.origin}/job/${roleId}` : '';
          setEditorContent(`Hi,\n\nWe have an excellent opportunity for the ${roleName} role. We reviewed your resume and found your profile to be very suitable for this position.${jobLink}\n\nWe would like to schedule a call with you to discuss this opportunity further.`);
        } else {
          setEditorContent('');
        }
      }
      
      let allSelected = [];
      let candidateEmails = [];

      candidates.forEach(c => {
         if (c.Document) allSelected.push(c.Document.DocumentId);
         else if (c.Documents?.length > 0) allSelected.push(c.Documents[0].DocumentId);

         if (autoPopulateCandidateEmails || isBulkEmailMode) {
           if (c.email) candidateEmails.push(c.email);
           else if (c.Contact && c.Contact.Email) candidateEmails.push(c.Contact.Email);
         }
      });
      setSelectedAttachments(allSelected);

      if ((autoPopulateCandidateEmails || isBulkEmailMode) && candidateEmails.length > 0) {
        // Filter out duplicates and empty values
        const uniqueEmails = [...new Set(candidateEmails.filter(Boolean))];
        setToInput(uniqueEmails.join(', '));
      } else {
        setToInput(''); // Clear or reset if needed, though usually empty on open
      }
    } else {
      setToInput('');
    }
  }, [candidates, autoPopulateCandidateEmails, isBulkEmailMode, roleName, roleId, selectedRole]);

  if (!isOpen) return null;

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (ccInput.trim()) {
        setCcList([...ccList, ccInput.trim()]);
        setCcInput('');
      }
    } else if (e.key === 'Backspace' && !ccInput && ccList.length > 0) {
      setCcList(ccList.slice(0, -1));
    }
  };

  const removeCc = (idx) => {
    setCcList(ccList.filter((_, i) => i !== idx));
  };

  const placeholderEditorIcons = [
    <Bold size={14} />, <Italic size={14} />, <Layout size={14} />, <List size={14} />, <Link size={14} />, <Image size={14} />
  ];

  const toggleAttachment = (docId) => {
    if (selectedAttachments.includes(docId)) {
      setSelectedAttachments(selectedAttachments.filter(id => id !== docId));
    } else {
      setSelectedAttachments([...selectedAttachments, docId]);
    }
  };

  const handleShare = async () => {
    if (!toInput.trim() && !autoPopulateCandidateEmails) {
      import('sweetalert2').then(mod => mod.default.fire('Error', 'Please enter a recipient email address', 'error'));
      return;
    }
    if (selectedAttachments.length === 0) {
      import('sweetalert2').then(mod => mod.default.fire('Error', 'Please select at least one attachment to share', 'error'));
      return;
    }

    try {
      setIsSending(true);
      const token = localStorage.getItem('token');
      
      if (autoPopulateCandidateEmails) {
        // Parse the 'To' field to see which emails the user actually kept
        const targetEmails = toInput.split(/[,;\s]+/).map(e => e.trim().toLowerCase()).filter(Boolean);

        // Send individual emails
        const promises = candidates.map(async (c) => {
          let candEmail = (c.email || c.Contact?.Email || '').toLowerCase();
          
          // If the candidate's email is not in the "To" box (because the user deleted it), skip them!
          if (!candEmail || !targetEmails.includes(candEmail)) return;

          // Find attachments for this specific candidate
          const cDocs = (c.Documents && c.Documents.length > 0) 
              ? c.Documents.map(d => d.DocumentId || d.document_id) 
              : (c.Document ? [c.Document.DocumentId] : []);
          const myDocs = selectedAttachments.filter(id => cDocs.includes(id));
          
          if (myDocs.length === 0) return; // Skip if no attachments for this candidate are selected

          // Convert plain text URLs into clickable HTML links, then convert newlines to <br/>
          const linkify = (text) => text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1">$1</a>');
          const formattedBody = linkify(editorContent).replace(/\n/g, '<br/>');

          const response = await fetch(`${BASE_URL}/api/resumes/share`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              candidateIds: [c.CandidateId],
              to: candEmail, // Send to this specific candidate
              cc: ccList,
              bcc: bccInput,
              subject: subject,
              body: formattedBody,
              documentIds: myDocs,
            }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Failed to send resume');
        });
        
        await Promise.all(promises);
      } else {
        // Convert plain text URLs into clickable HTML links, then convert newlines to <br/>
        const linkify = (text) => text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1">$1</a>');
        const formattedBody = linkify(editorContent).replace(/\n/g, '<br/>');

        // Original bulk send behavior
        const response = await fetch(`${BASE_URL}/api/resumes/share`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            candidateIds: candidates.map(c => c.CandidateId),
            to: toInput,
            cc: ccList,
            bcc: bccInput,
            subject: subject,
            body: formattedBody,
            documentIds: selectedAttachments,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to send resume');
      }

      import('sweetalert2').then((mod) => {
        mod.default.fire('Sent!', 'Emails have been sent successfully.', 'success');
        if (onSuccess) onSuccess();
        onClose();
      });
    } catch (error) {
      console.error('Error sharing resume:', error);
      import('sweetalert2').then((mod) => {
        mod.default.fire('Error', error.message || 'Failed to share resume', 'error');
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="share-modal-overlay">
      <div className="share-modal-container">
        {/* Header */}
        <div className="share-modal-header">
          <h2 className="share-modal-title">Share Resume</h2>
          <button className="share-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Content Body */}
        <div className="share-modal-content">
          {/* LEFT PANEL */}
          <div className="share-modal-left">
            
            <div className="share-form-group" style={{ marginTop: '8px' }}>
              <label className="share-label">Template</label>
              <select className="share-select-outline" value={template} onChange={e => setTemplate(e.target.value)}>
                <option>Resume Share</option>
                <option>System Default</option>
              </select>
            </div>

            <div className="share-form-group">
              <input 
                type="text" 
                className="share-input-outline" 
                placeholder="To" 
                value={toInput}
                onChange={e => setToInput(e.target.value)}
              />
            </div>

            <div className="share-form-group">
              <label className="share-label" style={{ background: 'white' }}>CC</label>
              <div className="share-cc-container">
                {ccList.map((cc, i) => (
                  <div key={i} className="share-cc-pill">
                    {cc}
                    <div className="share-cc-close" onClick={() => removeCc(i)}><X size={10} /></div>
                  </div>
                ))}
                <input 
                  type="text" 
                  className="share-cc-input"
                  value={ccInput}
                  onChange={e => setCcInput(e.target.value)}
                  onKeyDown={handleCcKeyDown}
                  placeholder={ccList.length === 0 ? "Add CC..." : ""}
                />
              </div>
            </div>

            <div className="share-form-group">
              <input 
                type="text" 
                className="share-input-outline" 
                placeholder="Bcc" 
                value={bccInput}
                onChange={e => setBccInput(e.target.value)}
              />
            </div>

            <div className="share-form-group">
              <label className="share-label" style={{ background: 'white' }}>Subject *</label>
              <input 
                type="text" 
                className="share-input-outline" 
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>

            <div className="share-editor-header" style={{ marginTop: '8px' }}>Mail Content</div>
            
            <div className="share-editor-container">
              <div className="share-editor-toolbar">
                <div className="share-toolbar-group">
                  {placeholderEditorIcons.map((icon, idx) => (
                    <button key={idx} className="share-toolbar-btn">{icon}</button>
                  ))}
                </div>
                <div className="share-toolbar-group">
                  <span style={{ fontSize: '13px', padding: '0 8px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    Styles <ChevronDown size={14} />
                  </span>
                </div>
                <div className="share-toolbar-group">
                  <span style={{ fontSize: '13px', padding: '0 8px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    Format <ChevronDown size={14} />
                  </span>
                </div>
                <div className="share-toolbar-group" style={{ border: 'none' }}>
                  <span style={{ fontSize: '13px', padding: '0 8px', color: '#4b5563', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <FileText size={14} /> Source
                  </span>
                </div>
              </div>
              <textarea 
                className="share-editor-textarea"
                value={editorContent}
                onChange={e => setEditorContent(e.target.value)}
              />
            </div>

          </div>

          {/* RIGHT PANEL: Mail Content */}
          <div className="share-modal-right">
            {isBulkEmailMode && (
              <div style={{ marginBottom: '15px' }}>
                <label className="share-label" style={{ background: 'white', marginBottom: '8px', display: 'block' }}>Select Role to Source For</label>
                {loadingRoles ? (
                  <div style={{ padding: '8px', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: '#3b5bfd' }} />
                  </div>
                ) : (
                  <Select
                    options={availableRoles.map(role => ({ value: role.id, label: role.title || role.role, role: role }))}
                    filterOption={(candidate, input) => {
                      if (input) {
                        const inputStr = input.toLowerCase();
                        const labelStr = String(candidate.label).toLowerCase();
                        const idStr = candidate.data.role?.id ? String(candidate.data.role.id).toLowerCase() : '';
                        const jobIdStr = candidate.data.role?.jobId ? String(candidate.data.role.jobId).toLowerCase() : '';
                        return labelStr.includes(inputStr) || idStr.includes(inputStr) || jobIdStr.includes(inputStr);
                      }
                      return true;
                    }}
                    onChange={(selectedOption) => {
                      setSelectedRole(selectedOption ? selectedOption.role : null);
                    }}
                    value={selectedRole ? { value: selectedRole.id, label: selectedRole.title || selectedRole.role } : null}
                    placeholder="Search and select a role..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#cbd5e1',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#94a3b8'
                        }
                      })
                    }}
                  />
                )}
              </div>
            )}

            <div className="share-attachment-title">Candidate Attachments</div>
            <div className="share-attachment-subtitle">Select the resumes/documents to share</div>
            
            <div className="share-attachments-list">
              {(() => {
                const docList = [];
                const cList = candidates || [];
                cList.forEach(c => {
                   if (c.Documents?.length > 0) {
                      c.Documents.forEach(d => docList.push({ ...d, candidateName: `${c.FirstName || ''} ${c.LastName || ''}`.trim() }));
                   } else if (c.Document) {
                      docList.push({ ...c.Document, candidateName: `${c.FirstName || ''} ${c.LastName || ''}`.trim() });
                   }
                });
                
                if (docList.length > 0) {
                  return docList.map((doc, idx) => (
                    <div 
                      key={`${doc.DocumentId}-${idx}`} 
                      className={`share-attachment-item ${selectedAttachments.includes(doc.DocumentId) ? 'selected' : ''}`}
                      onClick={() => toggleAttachment(doc.DocumentId)}
                      style={{ 
                        display: 'flex', alignItems: 'center', p: 2, padding: '10px', 
                        border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '8px', cursor: 'pointer',
                        background: selectedAttachments.includes(doc.DocumentId) ? '#eff6ff' : 'white',
                        borderColor: selectedAttachments.includes(doc.DocumentId) ? '#bfdbfe' : '#e5e7eb'
                      }}
                    >
                      <div style={{ marginRight: '12px', color: selectedAttachments.includes(doc.DocumentId) ? '#3b82f6' : '#d1d5db', display: 'flex' }}>
                        {selectedAttachments.includes(doc.DocumentId) ? <CheckSquare size={18} /> : <Square size={18} />}
                      </div>
                      <File size={20} color="#6b7280" style={{ marginRight: '8px' }} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#374151', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {doc.FileNameOriginal} {doc.candidateName ? `(${doc.candidateName})` : ''}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {doc.FileSizeBytes ? (doc.FileSizeBytes / 1024).toFixed(1) + ' KB • ' : ''}
                          {doc.VersionNo ? `v${doc.VersionNo}` : ''}
                          {doc.IsPrimary ? ' (Primary)' : ''}
                        </div>
                      </div>
                    </div>
                  ));
                } else {
                  return (
                    <div style={{ fontSize: '13px', color: '#6b7280', padding: '10px', background: '#f9fafb', borderRadius: '6px', textAlign: 'center' }}>
                      No documents available for selected candidate(s).
                    </div>
                  );
                }
              })()}
            </div>

            <label className="share-profile-checkbox">
              <input type="checkbox" style={{ accentColor: '#3b82f6', width: 16, height: 16 }} />
              Create Additional Profile for this Resume
            </label>

          </div>
        </div>

        {/* Footer */}
        <div className="share-modal-footer">
          <button className="share-btn-cancel" onClick={onClose} disabled={isSending}>Cancel</button>
          <button className="share-btn-submit" onClick={handleShare} disabled={isSending || selectedAttachments.length === 0}>
            {isSending ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Sending...
              </span>
            ) : 'Submit'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ShareResumeModal;
